import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRouteByPath, canAccessRoute } from "../config/routes";
import { ROUTE_PATHS } from "../config/routes";
import { getPermissionKeys } from "../utils/permissions";

interface PermissionRouteProps {
  children: React.ReactNode;
  permissions?: string[];
  requireAllPermissions?: boolean;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permissions,
  requireAllPermissions = false,
}) => {
  const {
    isAuthenticated,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    user,
  } = useAuth();
  const location = useLocation();

  // Check authentication first
  if (!isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  // Super admin has access to everything
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  // Check permissions if provided directly
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAllPermissions
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return <Navigate to="/forbidden" replace />;
    }
    return <>{children}</>;
  }

  // Check route config permissions
  const route = getRouteByPath(location.pathname);
  if (route && route.permissions && route.permissions.length > 0) {
    const userPermissions = getPermissionKeys(user?.role?.permissions);
    const userRoleValue = user?.role?.value;
    const hasAccess = canAccessRoute(
      route,
      userPermissions,
      isSuperAdmin(),
      userRoleValue,
    );

    if (!hasAccess) {
      return <Navigate to="/forbidden" replace />;
    }
  }

  return <>{children}</>;
};
